/* LA LOGICA DEL CONFRONTO FRA BACKTEST.
 *
 * Nessun JSX qui: sono le regole con cui si decide cosa è lecito confrontare e
 * cosa no, e stanno separate dalla schermata perché sono la parte che va
 * riletta e discussa, non ridisegnata.
 *
 * ==========================================================================
 * IL NODO: I PERIODI NON COINCIDONO
 * ==========================================================================
 *
 * Due backtest presi dalla stessa lista possono coprire intervalli diversi, e
 * le stesse configurazioni danno esiti opposti su 2017–2026 e su 2022–2026.
 * Affiancarne i numeri senza dirlo è il modo più efficace di far prendere una
 * decisione sbagliata. Quindi si fanno DUE letture, tenute separate e
 * etichettate, perché rispondono a due domande diverse:
 *
 *  1. IL PERIODO COMUNE — l'unica lettura rigorosa. Si prende l'intersezione e
 *     si confrontano solo gli anni che tutti hanno affrontato allo stesso modo.
 *     È l'unico contesto in cui «A è meglio di B» significa qualcosa, perché è
 *     l'unico in cui A e B hanno visto lo stesso mercato.
 *
 *  2. IL CONFRONTO GENERICO — utile, ma solo per le metriche già normalizzate.
 *     Il CAGR è annualizzato, «a pari rischio» è normalizzato al drawdown,
 *     intorno, sharpe, calmar e la percentuale di operazioni vinte sono
 *     rapporti: reggono anche su finestre di lunghezza diversa. Profitto
 *     totale, numero di operazioni e drawdown assoluto NON reggono, perché
 *     dipendono da quanto tempo hai misurato.
 *
 * ==========================================================================
 * IL LIMITE DELLA GRANA ANNUALE, CHE NON SI AGGIRA
 * ==========================================================================
 *
 * Del rendimento nel tempo abbiamo `yearlyChange`: un valore per anno solare,
 * non una serie giornaliera. La curva del capitale esiste solo sulle righe
 * eseguite singolarmente, e sulle righe da griglia — cioè quasi tutte — non
 * viene salvata.
 *
 * Conseguenza diretta: l'intersezione dei periodi si può usare solo a GRANA
 * ANNUALE. Se due backtest si sovrappongono per otto mesi a cavallo di due
 * anni, il rendimento di quella fetta non è ricavabile da due rendimenti
 * annuali senza inventare la parte che manca — quindi il confronto sul periodo
 * comune NON si fa, e si dice perché.
 *
 * Un anno entra nel periodo comune solo se TUTTI i candidati lo coprono per la
 * stessa fetta di calendario. Un backtest che parte in agosto 2017 e uno che
 * parte in gennaio 2017 hanno entrambi un «2017» in `yearlyChange`, ma sono
 * due 2017 diversi: metterli sulla stessa riga sarebbe un confronto fra cinque
 * mesi e dodici.
 *
 * L'anno in corso è coperto in parte da tutti allo stesso modo — i dati
 * finiscono dove finiscono le candele — quindi resta confrontabile, ma va
 * marcato come parziale ovunque compaia: un +12% su otto mesi non è un anno.
 */

import type { Backtest, RendimentoAnnuale } from '../../api/tipi';
import { drawdown as fmtDrawdown, num, pct, periodo as etichettaPeriodo } from '../../lib/formato';
import {
  aPariRischio,
  DRAWDOWN_OBIETTIVO,
  type Tono,
} from '../../lib/metriche';

/* ==========================================================================
 * IDENTITÀ DEI CANDIDATI
 * ======================================================================== */

/**
 * Ogni candidato porta una lettera, e la porta in OGNI sezione della pagina.
 *
 * Serve perché il colore non può fare questo lavoro: verde e rosso sono
 * riservati al significato finanziario, e usarli per dire «questa è la colonna
 * A» farebbe leggere un'identità come un giudizio. La lettera funziona anche
 * in bianco e nero, anche a chi non distingue i colori, e resta la stessa
 * nella tabella, nell'anno per anno, nelle curve e nei parametri.
 */
export const LETTERE = ['A', 'B', 'C', 'D'] as const;

/**
 * I tratti con cui si distinguono le curve sovrapposte.
 *
 * Tutti sulla rampa del blu — l'unico colore interattivo — più un tratteggio
 * diverso per ciascuno: due segnali indipendenti, così la curva si riconosce
 * anche stampata. Il verde e il rosso restano fuori: qui direbbero «guadagna»
 * di una linea che è solo la seconda.
 */
export const TRATTI: { colore: string; trattini: string | undefined }[] = [
  { colore: 'var(--sb-accento-piu-chiaro)', trattini: undefined },
  { colore: 'var(--sb-accento-chiaro)', trattini: '7 4' },
  { colore: 'var(--sb-accento)', trattini: '2 4' },
  { colore: 'var(--sb-accento-pieno)', trattini: '10 4 2 4' },
];

export interface Candidato {
  id: number;
  /** A, B, C, D: l'identità che attraversa tutta la pagina. */
  lettera: string;
  dato: Backtest;
}

/* ==========================================================================
 * DATE: TUTTO IN UTC, TUTTO IN MILLISECONDI
 * ======================================================================== */

const GIORNO = 86_400_000;

/**
 * Quanto due estremi possono differire e contare ancora come lo stesso giorno.
 *
 * Serve perché due griglie lanciate a giorni diversi salvano `endDate` diversi
 * di poche ore, e scartare un anno intero per quello sarebbe pedanteria che
 * costa informazione. Tre giorni su un anno solare sono meno dell'1% del
 * periodo: sotto quella soglia i due rendimenti annuali misurano la stessa
 * cosa. Sopra, no, e l'anno si scarta.
 */
export const TOLLERANZA_GIORNI = 3;
const TOLLERANZA = TOLLERANZA_GIORNI * GIORNO;

function istante(d: string | null | undefined): number | null {
  if (!d) return null;
  const t = new Date(d).getTime();
  return Number.isFinite(t) ? t : null;
}

function inizioAnno(anno: number): number {
  return Date.UTC(anno, 0, 1, 0, 0, 0, 0);
}

function fineAnno(anno: number): number {
  return Date.UTC(anno + 1, 0, 1, 0, 0, 0, 0) - 1;
}

function durataAnnoMs(anno: number): number {
  return fineAnno(anno) + 1 - inizioAnno(anno);
}

/** Gli anni misurati di un backtest, indicizzati per anno. */
function anniDi(b: Backtest): Map<number, RendimentoAnnuale> {
  const m = new Map<number, RendimentoAnnuale>();
  for (const a of b.yearlyChange ?? []) {
    if (!a || !/^\d{4}$/.test(String(a.year))) continue;
    m.set(Number(a.year), a);
  }
  return m;
}

/** Durata misurata in anni, per le metriche che vanno divise per il tempo. */
export function durataInAnni(b: Backtest): number | null {
  const a = istante(b.startDate);
  const z = istante(b.endDate);
  if (a == null || z == null || z <= a) return null;
  return (z - a) / (365.2425 * GIORNO);
}

/* ==========================================================================
 * L'ANNO PER ANNO AFFIANCATO
 * ======================================================================== */

/** Cosa è successo a un candidato in un anno. Le tre assenze sono diverse. */
export type StatoAnno =
  /** Misurato: c'è il rendimento. */
  | 'misurato'
  /** L'anno cade fuori dal periodo del backtest: non è un buco, è un confine. */
  | 'fuori'
  /** L'anno è nel periodo ma il rendimento non c'è: è un dato mancante. */
  | 'nonMisurato';

export interface CellaAnno {
  stato: StatoAnno;
  /** Rendimento della strategia. `null` se non misurato. */
  cambio: number | null;
  /** Rendimento del mercato nello stesso anno, per lo stesso candidato. */
  mercato: number | null;
  /** Frazione dell'anno solare coperta da questo candidato: 1 = anno intero. */
  frazione: number;
}

export interface RigaAnno {
  anno: number;
  celle: CellaAnno[];
  /** L'anno entra nel periodo comune: tutti lo coprono per la stessa fetta. */
  comune: boolean;
  /** Perché NON è comune. Frase pronta, `null` quando lo è. */
  motivo: string | null;
  /** Coperto solo in parte anche dove è comune: l'anno in corso. */
  parziale: boolean;
  /** Frazione d'anno del periodo comune. Serve ad annualizzare per davvero. */
  frazione: number;
  /** Chi ha reso di più in quest'anno, fra i soli candidati misurati. `null` a pari. */
  migliore: number | null;
}

export interface AnalisiAnni {
  /** Tutti gli anni toccati da almeno un candidato, in ordine crescente. */
  righe: RigaAnno[];
  /** Il sottoinsieme confrontabile, in ordine. */
  comuni: RigaAnno[];
  /** Somma delle frazioni: il denominatore onesto dell'annualizzazione. */
  anniEffettivi: number;
  /** L'intersezione di calendario, prima che la grana annuale la sfrondi. */
  dal: number | null;
  al: number | null;
  /** `null` quando il periodo comune è calcolabile; altrimenti dice perché no. */
  perche: string | null;
  /** Gli anni buttati via, col motivo: si mostrano, non si tacciono. */
  scartati: { anno: number; motivo: string }[];
  /**
   * Il mercato è lo stesso per tutti i candidati (stesso asset, stessi valori).
   * Quando lo è, la colonna del mercato è una sola; quando non lo è, ogni cella
   * si porta il proprio, perché due asset diversi hanno due metri diversi.
   */
  mercatoUnico: boolean;
}

/** Sotto questo scarto in punti, due rendimenti annuali si dicono pari. */
const PARI_ANNO = 0.5;

export function analizzaAnni(candidati: Candidato[]): AnalisiAnni {
  const mappe = candidati.map((c) => anniDi(c.dato));
  const inizi = candidati.map((c) => istante(c.dato.startDate));
  const fini = candidati.map((c) => istante(c.dato.endDate));

  /* L'intersezione di calendario: il tratto di tempo che tutti hanno vissuto.
     Si calcola comunque, anche quando la grana annuale poi la scarta, perché
     è quella che va scritta a schermo per spiegare il rifiuto. */
  const dal = inizi.every((t) => t != null) ? Math.max(...(inizi as number[])) : null;
  const al = fini.every((t) => t != null) ? Math.min(...(fini as number[])) : null;

  const anni = new Set<number>();
  for (const m of mappe) for (const a of m.keys()) anni.add(a);

  const righe: RigaAnno[] = [];
  const scartati: { anno: number; motivo: string }[] = [];

  for (const anno of [...anni].sort((x, y) => x - y)) {
    const apre = inizioAnno(anno);
    const chiude = fineAnno(anno);

    const celle: CellaAnno[] = candidati.map((_, i) => {
      const voce = mappe[i].get(anno);
      const a = inizi[i];
      const z = fini[i];
      const da = a == null ? apre : Math.max(a, apre);
      const a_ = z == null ? chiude : Math.min(z, chiude);
      const copre = a_ > da;
      const cambio = typeof voce?.change === 'number' && Number.isFinite(voce.change) ? voce.change : null;
      const mercato =
        typeof voce?.marketChange === 'number' && Number.isFinite(voce.marketChange)
          ? voce.marketChange
          : null;

      return {
        stato: cambio != null ? 'misurato' : copre ? 'nonMisurato' : 'fuori',
        cambio,
        mercato,
        frazione: copre ? (a_ - da) / durataAnnoMs(anno) : 0,
      };
    });

    /* --- l'anno entra nel periodo comune? Tre condizioni, tre motivi diversi
       da scrivere, perché «non confrontabile» senza il perché costringe a
       indovinare quale dei quattro candidati ha rotto il confronto. --- */
    let motivo: string | null = null;

    const fuori = candidati.filter((_, i) => celle[i].stato === 'fuori');
    if (fuori.length > 0) {
      motivo = `fuori dal periodo di ${fuori.map((c) => `«${c.lettera}»`).join(', ')}`;
    } else {
      /* Stessa fetta di calendario per tutti: due «2017» che partono a sei mesi
         di distanza sono due anni diversi con lo stesso nome. */
      const partenze = candidati.map((_, i) =>
        inizi[i] == null ? apre : Math.max(inizi[i] as number, apre),
      );
      const arrivi = candidati.map((_, i) =>
        fini[i] == null ? chiude : Math.min(fini[i] as number, chiude),
      );
      const scartoPartenza = Math.max(...partenze) - Math.min(...partenze);
      const scartoArrivo = Math.max(...arrivi) - Math.min(...arrivi);

      if (scartoPartenza > TOLLERANZA || scartoArrivo > TOLLERANZA) {
        const giorni = Math.round(Math.max(scartoPartenza, scartoArrivo) / GIORNO);
        motivo = `coperto per fette diverse: ${num(giorni)} giorni di scarto fra un candidato e l'altro`;
      } else {
        const mancanti = candidati.filter((_, i) => celle[i].stato === 'nonMisurato');
        if (mancanti.length > 0) {
          motivo = `rendimento non misurato su ${mancanti.map((c) => `«${c.lettera}»`).join(', ')}`;
        }
      }
    }

    const comune = motivo == null;
    if (!comune) scartati.push({ anno, motivo: motivo as string });

    /* La frazione del periodo comune è quella condivisa: con tutti allineati
       entro tolleranza, la minima è indistinguibile dalle altre. */
    const frazione = comune ? Math.min(...celle.map((c) => c.frazione)) : 0;

    /* Chi ha reso di più quest'anno, fra i soli candidati misurati: è la
       colonna che dice se il vantaggio è di tutti gli anni o di uno solo. */
    let migliore: number | null = null;
    const misurati = candidati
      .map((c, i) => ({ id: c.id, v: celle[i].cambio }))
      .filter((x): x is { id: number; v: number } => x.v != null);
    if (misurati.length >= 2) {
      const massimo = Math.max(...misurati.map((x) => x.v));
      const inTesta = misurati.filter((x) => massimo - x.v <= PARI_ANNO);
      migliore = inTesta.length === 1 ? inTesta[0].id : null;
    }

    righe.push({
      anno,
      celle,
      comune,
      motivo,
      parziale: comune && frazione < 0.98,
      frazione,
      migliore,
    });
  }

  const comuni = righe.filter((r) => r.comune);
  const anniEffettivi = comuni.reduce((s, r) => s + r.frazione, 0);

  /* --- perché il periodo comune non si può calcolare --- */
  let perche: string | null = null;
  if (comuni.length === 0) {
    if (righe.length === 0) {
      /* Nessun anno da nessuna parte: la causa non è l'intersezione, è che i
         rendimenti annuali non ci sono. Attribuirla al periodo — «si
         sovrappongono per sessanta mesi ma non su un anno solare» — sarebbe una
         spiegazione falsa, e la più insidiosa fra quelle possibili qui: manderebbe
         a cercare il problema nelle date invece che nel dato mancante. */
      perche =
        'Nessuno dei candidati porta i rendimenti anno per anno: il periodo comune si ricava da quelli, e senza di essi non c’è niente da intersecare — nemmeno quando i periodi coincidono. Prova a ricalcolare le metriche dal dettaglio di ciascuno.';
    } else if (dal == null || al == null) {
      perche =
        'Almeno un candidato non dichiara le date del periodo misurato: senza, non c’è intersezione da calcolare.';
    } else if (al <= dal) {
      perche =
        'I periodi non si sovrappongono affatto: i candidati hanno operato su tratti di storia diversi, e nessun numero li mette a confronto.';
    } else {
      const mesi = Math.max(1, Math.round((al - dal) / (30.44 * GIORNO)));
      perche =
        `I periodi si sovrappongono per circa ${num(mesi)} ${mesi === 1 ? 'mese' : 'mesi'}, ma non su un anno solare che tutti coprano allo stesso modo. ` +
        'Del rendimento nel tempo abbiamo il dato ANNO PER ANNO, non giorno per giorno: una sovrapposizione a cavallo di due anni non si può ricavare da due rendimenti annuali senza inventare la parte che manca. Il confronto sul periodo comune non si fa.';
    }
  }

  /* --- il mercato è lo stesso per tutti? Con asset diversi non lo è, e una
     colonna sola direbbe una cosa falsa. --- */
  const mercatoUnico =
    candidati.length > 0 &&
    candidati.every(
      (c) =>
        c.dato.asset === candidati[0].dato.asset && c.dato.currency === candidati[0].dato.currency,
    ) &&
    righe.every((r) => {
      const v = r.celle.map((c) => c.mercato).filter((m): m is number => m != null);
      return v.length === 0 || Math.max(...v) - Math.min(...v) <= PARI_ANNO;
    });

  return { righe, comuni, anniEffettivi, dal, al, perche, scartati, mercatoUnico };
}

/* ==========================================================================
 * IL RENDIMENTO SUL PERIODO COMUNE
 * ======================================================================== */

/** Composto dai rendimenti annuali: prodotto di (1 + r/100), meno uno. */
export function componi(cambi: number[]): number | null {
  if (cambi.length === 0) return null;
  let fattore = 1;
  for (const c of cambi) fattore *= 1 + c / 100;
  if (!Number.isFinite(fattore)) return null;
  return (fattore - 1) * 100;
}

/** Annualizza un composto su una durata frazionaria. Negativo oltre il −100% no. */
export function annualizza(compostoPct: number, anni: number): number | null {
  if (!(anni > 0)) return null;
  const base = 1 + compostoPct / 100;
  if (base < 0) return null;
  return (base ** (1 / anni) - 1) * 100;
}

export interface RendimentoComune {
  composto: number | null;
  annualizzato: number | null;
  mercato: number | null;
  mercatoAnnualizzato: number | null;
  /** Quanti degli anni comuni ha chiuso davanti al mercato. */
  anniSopraMercato: number | null;
}

export function rendimentoComune(
  analisi: AnalisiAnni,
  indice: number,
): RendimentoComune {
  const vuoto: RendimentoComune = {
    composto: null,
    annualizzato: null,
    mercato: null,
    mercatoAnnualizzato: null,
    anniSopraMercato: null,
  };
  if (analisi.comuni.length === 0) return vuoto;

  const cambi: number[] = [];
  const mercati: number[] = [];
  let sopra = 0;
  for (const r of analisi.comuni) {
    const c = r.celle[indice];
    if (c.cambio == null) return vuoto;
    cambi.push(c.cambio);
    if (c.mercato != null) {
      mercati.push(c.mercato);
      if (c.cambio >= c.mercato) sopra += 1;
    }
  }

  const composto = componi(cambi);
  /* Il mercato si compone solo se c'è per TUTTI gli anni comuni: un mercato
     composto su tre anni accanto a una strategia composta su quattro sarebbe
     un confronto fra due periodi diversi travestito da riga sola. */
  const mercato = mercati.length === analisi.comuni.length ? componi(mercati) : null;

  return {
    composto,
    annualizzato: composto == null ? null : annualizza(composto, analisi.anniEffettivi),
    mercato,
    mercatoAnnualizzato:
      mercato == null ? null : annualizza(mercato, analisi.anniEffettivi),
    anniSopraMercato: mercati.length > 0 ? sopra : null,
  };
}

/* ==========================================================================
 * LE METRICHE, E QUALI REGGONO IL CONFRONTO FRA PERIODI DIVERSI
 * ======================================================================== */

export type GruppoMetrica = 'rendimento' | 'rischio' | 'robustezza' | 'campione';

export const NOMI_GRUPPO: Record<GruppoMetrica, string> = {
  rendimento: 'Rendimento — quanto ha reso',
  rischio: 'Rischio — a che prezzo',
  robustezza: 'Robustezza — se ci si può fidare del numero',
  campione: 'Campione — su quante prove è misurato',
};

/** Come si decide chi è davanti. `nessuno` = la metrica non è un merito. */
export type Verso = 'alto' | 'basso' | 'nessuno';

/** Quando due valori si dichiarano pari. */
export type Soglia =
  /** Scarto relativo: due totali che differiscono dello 0,3% sono gemelli. */
  | { tipo: 'relativa'; valore: number }
  /** Scarto assoluto in unità della metrica: tre punti di intorno sono nulla. */
  | { tipo: 'assoluta'; valore: number };

export interface Metrica {
  chiave: string;
  nome: string;
  gruppo: GruppoMetrica;
  /**
   * `true` quando il valore è già normalizzato — annualizzato, rapportato al
   * rischio o al numero di prove — e quindi regge il confronto fra periodi di
   * lunghezza diversa. `false` quando è cumulativo e dipende da quanto tempo
   * hai misurato: mostrarne due affiancati senza dirlo è il modo di far
   * scegliere il backtest più lungo invece del migliore.
   */
  normalizzata: boolean;
  verso: Verso;
  soglia: Soglia;
  valore: (b: Backtest) => number | null;
  testo: (v: number | null) => string;
  /** Il tono finanziario della riga, quando ce l'ha. */
  tono: Tono | 'segno';
  /** Cosa significa, in una riga. Mai in un suggerimento al passaggio del mouse. */
  spiega: string;
}

function nd(v: number | null, scrivi: (n: number) => string): string {
  return v == null ? 'n/d' : scrivi(v);
}

export const METRICHE: Metrica[] = [
  /* ---------- rendimento ---------- */
  {
    chiave: 'strategyChange',
    nome: 'Profitto totale',
    gruppo: 'rendimento',
    normalizzata: false,
    verso: 'alto',
    soglia: { tipo: 'relativa', valore: 0.02 },
    valore: (b) => b.strategyChange,
    testo: (v) => nd(v, (n) => pct(n)),
    tono: 'segno',
    spiega: 'Cumulato su tutto il periodo: cresce col tempo misurato, non solo con la bontà della strategia.',
  },
  {
    chiave: 'marketChange',
    nome: 'Mercato nello stesso periodo',
    gruppo: 'rendimento',
    normalizzata: false,
    /* Il mercato è il metro, non un merito: non ha un vincitore da evidenziare. */
    verso: 'nessuno',
    soglia: { tipo: 'relativa', valore: 0.02 },
    valore: (b) => b.marketChange,
    testo: (v) => nd(v, (n) => pct(n)),
    tono: 'segno',
    spiega: 'Quanto ha fatto comprare e tenere sullo stesso asset e sullo stesso periodo.',
  },
  {
    chiave: 'cagr',
    nome: 'CAGR',
    gruppo: 'rendimento',
    normalizzata: true,
    verso: 'alto',
    soglia: { tipo: 'relativa', valore: 0.02 },
    valore: (b) => b.cagr,
    testo: (v) => nd(v, (n) => pct(n, 1)),
    tono: 'segno',
    spiega: 'Rendimento annualizzato: è la forma in cui due finestre di lunghezza diversa si possono accostare.',
  },
  {
    chiave: 'aPariRischio',
    nome: `A pari rischio (drawdown ${DRAWDOWN_OBIETTIVO}%)`,
    gruppo: 'rendimento',
    normalizzata: true,
    verso: 'alto',
    soglia: { tipo: 'relativa', valore: 0.02 },
    valore: (b) =>
      b.strategyChange == null || b.drawdownMaxValue == null || b.drawdownMaxValue === 0
        ? null
        : aPariRischio(b.strategyChange, b.drawdownMaxValue),
    testo: (v) => nd(v, (n) => `${num(n)}%`),
    tono: 'neutro',
    spiega: `Il profitto riportato per deleveraggio a un drawdown del ${DRAWDOWN_OBIETTIVO}%: è la classifica che ribalta quella del profitto nudo.`,
  },

  /* ---------- rischio ---------- */
  {
    chiave: 'drawdownMaxValue',
    nome: 'Drawdown massimo',
    gruppo: 'rischio',
    normalizzata: false,
    verso: 'basso',
    soglia: { tipo: 'assoluta', valore: 1 },
    valore: (b) => (b.drawdownMaxValue == null ? null : Math.abs(b.drawdownMaxValue)),
    testo: (v) => nd(v, (n) => fmtDrawdown(n)),
    tono: 'perdita',
    spiega: 'La perdita peggiore dal picco: più anni misuri, più occasioni ha avuto di essere profonda.',
  },
  {
    chiave: 'calmar',
    nome: 'Calmar',
    gruppo: 'rischio',
    normalizzata: true,
    verso: 'alto',
    soglia: { tipo: 'relativa', valore: 0.05 },
    valore: (b) => b.calmar,
    testo: (v) => nd(v, (n) => num(n, 2)),
    tono: 'neutro',
    spiega: 'Rendimento annualizzato diviso il drawdown: quanto rende ogni punto di rischio.',
  },
  {
    chiave: 'sharpe',
    nome: 'Sharpe',
    gruppo: 'rischio',
    normalizzata: true,
    verso: 'alto',
    soglia: { tipo: 'relativa', valore: 0.05 },
    valore: (b) => b.sharpe,
    testo: (v) => nd(v, (n) => num(n, 2)),
    tono: 'neutro',
    spiega: 'Rendimento per unità di oscillazione: penalizza chi arriva allo stesso posto sobbalzando.',
  },

  /* ---------- robustezza ---------- */
  {
    chiave: 'intorno',
    nome: 'Intorno',
    gruppo: 'robustezza',
    normalizzata: true,
    verso: 'alto',
    soglia: { tipo: 'assoluta', valore: 3 },
    valore: (b) => b.intorno,
    testo: (v) => nd(v, (n) => `${num(n)}%`),
    tono: 'neutro',
    spiega: 'Quanto resta del risultato spostando un parametro di una tacca: sotto il 30% è un picco, non una strategia.',
  },
  {
    chiave: 'walkForwardScore',
    nome: 'Tenuta',
    gruppo: 'robustezza',
    normalizzata: false,
    verso: 'alto',
    soglia: { tipo: 'assoluta', valore: 5 },
    /* Il server manda un RAPPORTO: 1 significa «rende quanto prima». A schermo
       vive in percentuale, come in tutta l'applicazione. */
    valore: (b) => (b.walkForwardScore == null ? null : b.walkForwardScore * 100),
    testo: (v) => nd(v, (n) => `${num(n)}%`),
    tono: 'neutro',
    spiega: 'Quanto rende la seconda metà del periodo rispetto alla prima. Su periodi diversi divide storie diverse.',
  },
  {
    chiave: 'bearYearsAvg',
    nome: 'Nei ribassi',
    gruppo: 'robustezza',
    normalizzata: false,
    verso: 'alto',
    soglia: { tipo: 'assoluta', valore: 2 },
    valore: (b) => b.bearYearsAvg,
    testo: (v) => nd(v, (n) => pct(n)),
    tono: 'segno',
    spiega: 'Media dei soli anni in cui il mercato ha perso. Dipende da quali e quanti ribassi cadono nel periodo.',
  },

  /* ---------- campione ---------- */
  {
    chiave: 'tradesNumber',
    nome: 'Operazioni',
    gruppo: 'campione',
    normalizzata: false,
    verso: 'nessuno',
    soglia: { tipo: 'relativa', valore: 0.05 },
    valore: (b) => b.tradesNumber,
    testo: (v) => nd(v, (n) => num(n)),
    tono: 'neutro',
    spiega: 'Quante prove ha fatto la strategia. Un totale più alto può voler dire solo un periodo più lungo.',
  },
  {
    chiave: 'opsAnno',
    nome: 'Operazioni per anno',
    gruppo: 'campione',
    normalizzata: true,
    verso: 'nessuno',
    soglia: { tipo: 'relativa', valore: 0.05 },
    valore: (b) => {
      const anni = durataInAnni(b);
      return anni == null || b.tradesNumber == null ? null : b.tradesNumber / anni;
    },
    testo: (v) => nd(v, (n) => num(n, 1)),
    tono: 'neutro',
    spiega: 'Le operazioni divise per gli anni misurati: è il numero che rende confrontabili una candela da un\'ora e una da quattro.',
  },
  {
    chiave: 'winTradesPercentage',
    nome: 'Operazioni vinte',
    gruppo: 'campione',
    normalizzata: true,
    verso: 'alto',
    soglia: { tipo: 'assoluta', valore: 2 },
    valore: (b) => b.winTradesPercentage,
    testo: (v) => nd(v, (n) => `${num(n, 1)}%`),
    tono: 'neutro',
    spiega: 'Percentuale di operazioni chiuse in guadagno: è già un rapporto, quindi regge fra periodi diversi.',
  },
];

/** `true` quando i candidati hanno misurato esattamente la stessa finestra. */
export function periodiIdentici(candidati: Candidato[]): boolean {
  if (candidati.length < 2) return true;
  const inizi = candidati.map((c) => istante(c.dato.startDate));
  const fini = candidati.map((c) => istante(c.dato.endDate));
  if (inizi.some((t) => t == null) || fini.some((t) => t == null)) return false;
  const i = inizi as number[];
  const f = fini as number[];
  return (
    Math.max(...i) - Math.min(...i) <= TOLLERANZA && Math.max(...f) - Math.min(...f) <= TOLLERANZA
  );
}

/**
 * Il confronto su questa riga è lecito?
 *
 * Due casi soli: o la metrica è già normalizzata, o i periodi coincidono. In
 * ogni altro caso il «migliore» sarebbe il più fortunato con la finestra, e
 * evidenziarlo sarebbe un'affermazione che i dati non sostengono.
 */
export function confrontoLecito(m: Metrica, periodiUguali: boolean): boolean {
  return m.normalizzata || periodiUguali;
}

/* ==========================================================================
 * CHI È DAVANTI SU UNA RIGA
 * ======================================================================== */

export interface EsitoRiga {
  /** Gli id in testa. Più di uno significa pareggio. */
  inTesta: number[];
  /** `true` quando la riga è un pareggio: nessuno da evidenziare. */
  pari: boolean;
  /** Lo scarto fra il primo e il secondo, già scritto. `null` se indecidibile. */
  scarto: string | null;
  /** Lo scarto grezzo, col segno del vantaggio del primo. */
  scartoGrezzo: number | null;
}

function differenzaOltreSoglia(a: number, b: number, s: Soglia): boolean {
  const d = Math.abs(a - b);
  if (s.tipo === 'assoluta') return d > s.valore;
  const scala = Math.max(Math.abs(a), Math.abs(b));
  if (scala === 0) return false;
  return d / scala > s.valore;
}

/**
 * Chi è in testa fra una fila di valori, per indice. `null` a pari o quando i
 * misurati sono meno di due.
 *
 * Esiste separata da `esitoRiga` perché il periodo comune non è una `Metrica`:
 * i suoi numeri sono calcolati sull'intersezione, non letti da una riga. La
 * regola del pareggio però deve restare la stessa — due composti che
 * differiscono dello 0,3% sono gemelli lì come in tabella — e due soglie
 * diverse per la stessa domanda darebbero due verdetti diversi nella stessa
 * pagina.
 */
export function inTestaFra(
  valori: (number | null)[],
  soglia: Soglia = { tipo: 'relativa', valore: 0.02 },
): number | null {
  const misurati = valori
    .map((v, i) => ({ i, v }))
    .filter((x): x is { i: number; v: number } => x.v != null && Number.isFinite(x.v));
  if (misurati.length < 2) return null;
  const ordinati = [...misurati].sort((a, b) => b.v - a.v);
  if (!differenzaOltreSoglia(ordinati[0].v, ordinati[1].v, soglia)) return null;
  return ordinati[0].i;
}

export function esitoRiga(m: Metrica, candidati: Candidato[]): EsitoRiga {
  const vuoto: EsitoRiga = { inTesta: [], pari: true, scarto: null, scartoGrezzo: null };
  if (m.verso === 'nessuno') return vuoto;

  const misurati = candidati
    .map((c) => ({ id: c.id, v: m.valore(c.dato) }))
    .filter((x): x is { id: number; v: number } => x.v != null && Number.isFinite(x.v));
  if (misurati.length < 2) return vuoto;

  const ordinati = [...misurati].sort((a, b) => (m.verso === 'alto' ? b.v - a.v : a.v - b.v));
  const primo = ordinati[0];
  const secondo = ordinati[1];

  const inTesta = ordinati
    .filter((x) => !differenzaOltreSoglia(x.v, primo.v, m.soglia))
    .map((x) => x.id);
  const pari = inTesta.length > 1;

  /* Lo scarto è sempre quello fra il primo e il secondo: è la distanza che
     conta per decidere, e lasciarla all'occhio su due numeri a cinque cifre
     è esattamente il lavoro che questa pagina esiste per fare. */
  const grezzo = m.verso === 'alto' ? primo.v - secondo.v : secondo.v - primo.v;

  return {
    inTesta: pari ? [] : [primo.id],
    pari,
    scartoGrezzo: grezzo,
    scarto: scriviScarto(m, primo.v, secondo.v),
  };
}

/** Lo scarto fra i due in testa, nelle unità della metrica. */
function scriviScarto(m: Metrica, primo: number, secondo: number): string {
  const d = Math.abs(primo - secondo);
  /* Zero non è uno scarto: «0,0 punti di scarto» fa cercare una differenza che
     non c'è. Quando i due valori coincidono si dice che coincidono. */
  if (d === 0) return 'identici';
  /* In punti quando la metrica è una percentuale, in valore assoluto quando è
     un rapporto: «0,4 di Calmar» e «0,4 punti» sono due cose diverse. */
  if (m.chiave === 'calmar' || m.chiave === 'sharpe') return `${num(d, 2)} di scarto`;
  if (m.chiave === 'tradesNumber') return `${num(d)} operazioni`;
  if (m.chiave === 'opsAnno') return `${num(d, 1)} all'anno`;
  if (m.chiave === 'strategyChange' || m.chiave === 'marketChange' || m.chiave === 'aPariRischio') {
    /* Su numeri a cinque cifre lo scarto in punti non dice niente: quello che
       si legge è «quante volte». */
    const rapporto =
      Math.min(Math.abs(primo), Math.abs(secondo)) === 0
        ? null
        : Math.max(Math.abs(primo), Math.abs(secondo)) / Math.min(Math.abs(primo), Math.abs(secondo));
    if (rapporto != null && rapporto >= 1.15) return `${num(rapporto, 2)}× di scarto`;
    return `${num(d, 1)} punti di scarto`;
  }
  return `${num(d, 1)} punti di scarto`;
}

/* ==========================================================================
 * IL VERDETTO
 * ==========================================================================
 *
 * Non «A vince», che è quello che si vede già ordinando la lista. La domanda è
 * PERCHÉ, cioè su quali assi uno è davanti e su quali resta indietro — e quando
 * i due si dividono gli assi, la risposta giusta è che non c'è un vincitore.
 * Non è un fallimento della pagina: due strategie che vincono su assi diversi
 * sono due strumenti per due mercati.
 */

export interface Asse {
  chiave: string;
  /** Come si nomina nella frase: «davanti su rendimento a pari rischio». */
  frase: string;
  vincitore: number | null;
  pari: boolean;
  /** I valori scritti, nell'ordine dei candidati. */
  valori: string[];
  scarto: string | null;
}

export interface Verdetto {
  vincitore: number | null;
  /** La frase intera, pronta da mostrare. */
  frase: string;
  assi: Asse[];
  /** Le riserve da leggere PRIMA della frase: periodi, mercati, anno parziale. */
  riserve: string[];
  /** Le osservazioni che il totale nasconde: gemelli, anno decisivo. */
  osservazioni: string[];
}

/** I nomi con cui gli assi entrano nella frase del verdetto. */
const FRASI_ASSE: Record<string, string> = {
  comune: 'rendimento sul periodo comune',
  cagr: 'rendimento annualizzato',
  aPariRischio: 'rendimento a pari rischio',
  walkForwardScore: 'tenuta',
  intorno: 'intorno',
  bearYearsAvg: 'tenuta nei ribassi',
};

function elenca(voci: string[]): string {
  if (voci.length === 0) return '';
  if (voci.length === 1) return voci[0];
  return `${voci.slice(0, -1).join(', ')} e ${voci[voci.length - 1]}`;
}

export function calcolaVerdetto(
  candidati: Candidato[],
  analisi: AnalisiAnni,
  comuni: RendimentoComune[],
): Verdetto {
  const periodiUguali = periodiIdentici(candidati);
  const perChiave = new Map(METRICHE.map((m) => [m.chiave, m]));
  const assi: Asse[] = [];

  /* --- l'asse rigoroso: il rendimento sul periodo comune. Quando esiste, è il
     primo e vale più degli altri, perché è l'unico misurato sullo stesso
     mercato. Quando non esiste, il suo posto lo prende il CAGR, che è
     annualizzato e quindi almeno confrontabile. --- */
  if (analisi.comuni.length > 0 && comuni.some((c) => c.composto != null)) {
    const valori = comuni.map((c) => c.composto);
    assi.push(
      asseDaValori('comune', candidati, valori, { tipo: 'relativa', valore: 0.02 }, (v) =>
        v == null ? 'n/d' : pct(v),
      ),
    );
  } else {
    const m = perChiave.get('cagr') as Metrica;
    assi.push(asseDaMetrica('cagr', m, candidati));
  }

  /* --- gli altri assi. Uno che dipende dal periodo entra solo se i periodi
     coincidono: altrimenti si starebbe premiando la finestra, non la
     strategia. --- */
  for (const chiave of ['aPariRischio', 'walkForwardScore', 'intorno', 'bearYearsAvg']) {
    const m = perChiave.get(chiave);
    if (!m) continue;
    if (!confrontoLecito(m, periodiUguali)) continue;
    assi.push(asseDaMetrica(chiave, m, candidati));
  }

  const decisi = assi.filter((a) => a.vincitore != null);
  const conteggi = new Map<number, number>();
  for (const a of decisi) conteggi.set(a.vincitore as number, (conteggi.get(a.vincitore as number) ?? 0) + 1);

  let vincitore: number | null = null;
  if (decisi.length > 0) {
    const massimo = Math.max(...candidati.map((c) => conteggi.get(c.id) ?? 0));
    const inTesta = candidati.filter((c) => (conteggi.get(c.id) ?? 0) === massimo);
    /* Vincitore solo se uno solo è in testa E ha almeno metà degli assi decisi:
       chi vince due assi su cinque mentre gli altri tre vanno a due avversari
       diversi non ha battuto nessuno. */
    if (inTesta.length === 1 && massimo > 0 && massimo * 2 >= decisi.length) {
      vincitore = inTesta[0].id;
    }
  }

  const lettera = (id: number) => candidati.find((c) => c.id === id)?.lettera ?? '?';
  const assiDi = (id: number) => decisi.filter((a) => a.vincitore === id).map((a) => a.frase);

  let frase: string;
  if (decisi.length === 0) {
    frase =
      'Non c’è un verdetto da dare: nessuno degli assi di giudizio è misurato su tutti i candidati. Quello che manca è scritto riga per riga qui sotto.';
  } else if (vincitore != null) {
    const perdenti = candidati.filter((c) => c.id !== vincitore);
    const suoi = assiDi(vincitore);
    const contro = perdenti.map((c) => c.lettera).join(', ');
    const rivincite = perdenti
      .filter((c) => assiDi(c.id).length > 0)
      .map((c) => `«${c.lettera}» resta davanti su ${elenca(assiDi(c.id))}`);
    frase =
      `«${lettera(vincitore)}» batte ${perdenti.length === 1 ? `«${contro}»` : `gli altri`} su ${elenca(suoi)}` +
      (rivincite.length > 0 ? `; ${rivincite.join('; ')}.` : `, e non resta indietro su nessun asse.`);
  } else {
    const pezzi = candidati
      .filter((c) => assiDi(c.id).length > 0)
      .map((c) => `«${c.lettera}» su ${elenca(assiDi(c.id))}`);
    frase =
      pezzi.length === 0
        ? 'Nessun vincitore: su tutti gli assi di giudizio i candidati si equivalgono. La differenza, se c’è, sta nell’anno per anno.'
        : `Nessun vincitore: ${elenca(pezzi)}. Sono strumenti per mercati diversi, non gradi della stessa cosa — la scelta dipende da quale dei due assi conta per te.`;
  }

  return {
    vincitore,
    frase,
    assi,
    riserve: riserve(candidati, analisi, periodiUguali),
    osservazioni: osservazioni(candidati, analisi, comuni),
  };
}

function asseDaMetrica(chiave: string, m: Metrica, candidati: Candidato[]): Asse {
  const esito = esitoRiga(m, candidati);
  return {
    chiave,
    frase: FRASI_ASSE[chiave] ?? m.nome.toLowerCase(),
    vincitore: esito.inTesta.length === 1 ? esito.inTesta[0] : null,
    pari: esito.pari,
    valori: candidati.map((c) => m.testo(m.valore(c.dato))),
    scarto: esito.scarto,
  };
}

function asseDaValori(
  chiave: string,
  candidati: Candidato[],
  valori: (number | null)[],
  soglia: Soglia,
  scrivi: (v: number | null) => string,
): Asse {
  const misurati = candidati
    .map((c, i) => ({ id: c.id, v: valori[i] }))
    .filter((x): x is { id: number; v: number } => x.v != null && Number.isFinite(x.v));

  if (misurati.length < 2) {
    return {
      chiave,
      frase: FRASI_ASSE[chiave] ?? chiave,
      vincitore: null,
      pari: true,
      valori: valori.map(scrivi),
      scarto: null,
    };
  }

  const ordinati = [...misurati].sort((a, b) => b.v - a.v);
  const pari = !differenzaOltreSoglia(ordinati[0].v, ordinati[1].v, soglia);
  return {
    chiave,
    frase: FRASI_ASSE[chiave] ?? chiave,
    vincitore: pari ? null : ordinati[0].id,
    pari,
    valori: valori.map(scrivi),
    scarto: pari
      ? null
      : `${num(Math.abs(ordinati[0].v - ordinati[1].v), 1)} punti di scarto`,
  };
}

/* --------------------------------------------------------------------------
 * Le riserve: quello che va letto PRIMA del verdetto, non dopo.
 * ------------------------------------------------------------------------ */

function riserve(candidati: Candidato[], analisi: AnalisiAnni, periodiUguali: boolean): string[] {
  const r: string[] = [];

  /* Senza periodo comune il verdetto perde il suo asse rigoroso e si appoggia
     alle sole metriche normalizzate. Dice ancora qualcosa — quale ha reso
     meglio per unità di tempo e di rischio — ma non dice quale avrebbe reso
     meglio sullo stesso mercato, che è la domanda che si sta facendo. Va
     scritto sopra il verdetto, non sotto. */
  if (analisi.perche != null) {
    r.push(
      'Il periodo comune non è calcolabile, quindi il verdetto qui sotto si appoggia soltanto alle metriche normalizzate: dice quale ha reso meglio per unità di tempo e di rischio, non quale avrebbe reso meglio sullo stesso mercato.',
    );
  }

  const mercati = [...new Set(candidati.map((c) => `${c.dato.asset}/${c.dato.currency}`))];
  if (mercati.length > 1) {
    r.push(
      `Mercati diversi (${mercati.join(', ')}): il profitto dice anche quanto è salito l’asset, non solo quanto vale la strategia. Guarda lo scarto dal mercato, non il totale.`,
    );
  }

  if (!periodiUguali) {
    const periodi = candidati.map(
      (c) => `«${c.lettera}» ${etichettaPeriodo(c.dato.startDate, c.dato.endDate)}`,
    );
    r.push(
      `Periodi di misura diversi: ${periodi.join(' · ')}. Le righe cumulative — profitto, operazioni, drawdown — restano affiancate ma non hanno un vincitore evidenziato: sarebbe il premio alla finestra più lunga.`,
    );
  }

  const strategie = [...new Set(candidati.map((c) => c.dato.strategyName))];
  if (strategie.length > 1) {
    r.push(
      `Strategie diverse (${strategie.join(', ')}): i parametri non sono accostabili e la domanda utile non è quale rende di più, ma DOVE una batte l’altra. L’anno per anno è la parte da leggere.`,
    );
  }

  const parziali = analisi.comuni.filter((a) => a.parziale).map((a) => a.anno);
  if (parziali.length > 0) {
    r.push(
      `${parziali.length === 1 ? `Il ${parziali[0]} è un anno parziale` : `Gli anni ${parziali.join(', ')} sono parziali`}: i dati finiscono dove finiscono le candele. Il rendimento è quello del tratto misurato, non di dodici mesi.`,
    );
  }

  return r;
}

/* --------------------------------------------------------------------------
 * Le osservazioni: quello che il totale nasconde e l'anno per anno rivela.
 * ------------------------------------------------------------------------ */

function osservazioni(
  candidati: Candidato[],
  analisi: AnalisiAnni,
  comuni: RendimentoComune[],
): string[] {
  const o: string[] = [];
  if (candidati.length < 2) return o;

  /* --- I GEMELLI. Due configurazioni con lo stesso totale e comportamento
     opposto: il caso XRP a quattro ore, +15.445% contro +15.398%, che il totale
     mostra identiche e l'anno per anno separa. Se i totali si equivalgono ma un
     anno qualsiasi li divide di molto, la pagina lo deve dire: è l'unica
     informazione per cui vale la pena aprirla.

     Massimo due coppie: con quattro candidati le coppie sono sei, e sei frasi
     dello stesso tenore non si leggono più — diventano un paragrafo da saltare,
     cioè il contrario di un'osservazione. --- */
  for (let i = 0; i < candidati.length && o.length < 2; i += 1) {
    for (let j = i + 1; j < candidati.length && o.length < 2; j += 1) {
      const a = candidati[i].dato.strategyChange;
      const b = candidati[j].dato.strategyChange;
      if (a == null || b == null) continue;
      if (differenzaOltreSoglia(a, b, { tipo: 'relativa', valore: 0.02 })) continue;

      let peggiore = 0;
      let annoPeggiore: number | null = null;
      for (const r of analisi.righe) {
        const x = r.celle[i].cambio;
        const y = r.celle[j].cambio;
        if (x == null || y == null) continue;
        if (Math.abs(x - y) > peggiore) {
          peggiore = Math.abs(x - y);
          annoPeggiore = r.anno;
        }
      }
      if (annoPeggiore != null && peggiore >= 10) {
        o.push(
          `«${candidati[i].lettera}» e «${candidati[j].lettera}» chiudono sullo stesso totale (${pct(a)} contro ${pct(b)}) ma non sono la stessa cosa: nel ${annoPeggiore} le separano ${num(peggiore, 1)} punti. Il totale le mostra gemelle, l’anno per anno no.`,
        );
      }
    }
  }

  /* --- L'ANNO DECISIVO. Un 2024 a +484% può gonfiare da solo il vantaggio di
     una configurazione su tutta una griglia. Il vantaggio composto si scompone
     in somma di logaritmi anno per anno: se un anno solo ne porta più della
     metà, il vantaggio non è strutturale ed è la prima cosa da sapere.

     Si guarda SOLO la coppia capofila–inseguitore sul periodo comune. Su tutte
     le coppie ordinate uscirebbero fino a dodici frasi quasi identiche, e la
     domanda a cui questa osservazione risponde — «il primo è primo per merito o
     per un anno?» — riguarda il primo. --- */
  const classifica = candidati
    .map((c, i) => ({ i, c, v: comuni[i]?.composto ?? null }))
    .filter((x): x is { i: number; c: Candidato; v: number } => x.v != null)
    .sort((x, y) => y.v - x.v);

  if (analisi.comuni.length >= 2 && classifica.length >= 2) {
    const primo = classifica[0];
    const secondo = classifica[1];
    const contributi: { anno: number; log: number }[] = [];
    let totale = 0;
    let completo = true;
    for (const r of analisi.comuni) {
      const x = r.celle[primo.i].cambio;
      const y = r.celle[secondo.i].cambio;
      if (x == null || y == null || 1 + x / 100 <= 0 || 1 + y / 100 <= 0) {
        completo = false;
        break;
      }
      const d = Math.log(1 + x / 100) - Math.log(1 + y / 100);
      contributi.push({ anno: r.anno, log: d });
      totale += d;
    }

    if (completo && totale > 0 && contributi.length >= 2) {
      const top = contributi.reduce((m, x) => (x.log > m.log ? x : m), contributi[0]);
      if (top.log / totale > 0.5) {
        /* La quota può superare il 100%: succede quando gli altri anni vanno
           nella direzione opposta. In quel caso «viene per il 121% da un anno»
           non significa niente, e la frase che seguiva — «sul resto li separa di
           poco» — era addirittura falsa: sul resto è l'altro a essere davanti.
           Il rapporto residuo lo dice per quello che è. */
        const residuo = Math.exp(totale - top.log);
        o.push(
          residuo <= 1
            ? `Tutto il vantaggio di «${primo.c.lettera}» su «${secondo.c.lettera}» viene dal solo ${top.anno}: sugli altri anni del periodo comune è «${secondo.c.lettera}» a essere davanti, di ${num(1 / residuo, 2)}×. Non è una superiorità strutturale, è un anno.`
            : `Il vantaggio di «${primo.c.lettera}» su «${secondo.c.lettera}» viene per il ${Math.round((top.log / totale) * 100)}% dal solo ${top.anno}: togliendo quell’anno si riduce a ${num(residuo, 2)}×. È un vantaggio di un anno più che una superiorità strutturale.`,
        );
      }
    }
  }

  return o;
}

/* ==========================================================================
 * I PARAMETRI A CONFRONTO
 * ======================================================================== */

export interface RigaParametro {
  chiave: string;
  /** Il valore scritto, per ogni candidato. `null` = il parametro non c'è. */
  valori: (string | null)[];
  /** `true` quando almeno due candidati hanno valori diversi. */
  diverso: boolean;
  /** `true` per candela e warmup, che non sono parametri di strategia. */
  esecuzione: boolean;
}

export interface ConfrontoParametri {
  /** `false` quando le strategie non sono la stessa: le colonne non hanno senso. */
  accostabili: boolean;
  strategie: string[];
  righe: RigaParametro[];
  /** Le sole righe che differiscono: è lì che si sta osservando qualcosa. */
  differenze: RigaParametro[];
}

function scriviValore(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function confrontaParametri(candidati: Candidato[]): ConfrontoParametri {
  const strategie = [...new Set(candidati.map((c) => c.dato.strategyName))];
  const accostabili = strategie.length === 1;

  const chiavi = new Set<string>();
  for (const c of candidati) for (const k of Object.keys(c.dato.strategyParams ?? {})) chiavi.add(k);

  const righe: RigaParametro[] = [];

  /* Candela e warmup stanno in cima e sono marcate come esecuzione: non sono
     parametri della strategia, ma la stessa strategia su due candele è
     esattamente il terzo caso per cui questa pagina esiste — e se la sola
     differenza è la candela, è quella la variabile in osservazione. */
  righe.push(rigaDa('candela (minuti)', candidati.map((c) => String(c.dato.candleSize)), true));
  righe.push(rigaDa('warmup', candidati.map((c) => String(c.dato.warmupPeriod)), true));

  for (const k of [...chiavi].sort()) {
    righe.push(
      rigaDa(
        k,
        candidati.map((c) => {
          const p = c.dato.strategyParams ?? {};
          return k in p ? scriviValore(p[k]) : null;
        }),
        false,
      ),
    );
  }

  return { accostabili, strategie, righe, differenze: righe.filter((r) => r.diverso) };
}

function rigaDa(chiave: string, valori: (string | null)[], esecuzione: boolean): RigaParametro {
  const distinti = new Set(valori.map((v) => v ?? ' assente'));
  return { chiave, valori, diverso: distinti.size > 1, esecuzione };
}

/* ==========================================================================
 * LE CURVE DI EQUITY SOVRAPPOSTE
 * ======================================================================== */

export interface SerieEquity {
  id: number;
  lettera: string;
  /** Punti già indicizzati a 100 sul proprio primo valore. */
  punti: { t: number; v: number }[];
}

/**
 * Le curve pronte da sovrapporre, e chi non ce l'ha.
 *
 * Due normalizzazioni obbligate, entrambe da dichiarare a schermo:
 *  - l'asse X è il TEMPO REALE, non l'indice del punto: due backtest con
 *    periodi diversi devono partire in due posti diversi, altrimenti il grafico
 *    li allineerebbe come se avessero vissuto gli stessi mesi;
 *  - l'asse Y è INDICIZZATO A 100 sul primo punto di ciascuno: i capitali
 *    iniziali possono differire, e una curva più alta perché è partita da più
 *    soldi non dice niente su quale strategia sia migliore.
 *
 * Il campionamento serve: `equityLinePoints` può avere decine di migliaia di
 * coppie, e quattro serie intere in altrettanti `<path>` bloccano il thread
 * principale. Si prende un punto ogni N mantenendo primo e ultimo, così gli
 * estremi restano esatti.
 */
export function serieEquity(
  candidati: Candidato[],
  massimoPunti = 600,
): { serie: SerieEquity[]; senzaCurva: Candidato[] } {
  const serie: SerieEquity[] = [];
  const senzaCurva: Candidato[] = [];

  for (const c of candidati) {
    const grezzi = (c.dato.equityLinePoints ?? []).filter(
      (p) => p && Number.isFinite(p.timestamp) && Number.isFinite(p.value) && p.value > 0,
    );
    if (grezzi.length < 2) {
      senzaCurva.push(c);
      continue;
    }
    const base = grezzi[0].value;
    const passo = Math.max(1, Math.ceil(grezzi.length / massimoPunti));
    const punti: { t: number; v: number }[] = [];
    for (let i = 0; i < grezzi.length; i += passo) {
      punti.push({ t: grezzi[i].timestamp, v: (grezzi[i].value / base) * 100 });
    }
    const ultimo = grezzi[grezzi.length - 1];
    if (punti[punti.length - 1].t !== ultimo.timestamp) {
      punti.push({ t: ultimo.timestamp, v: (ultimo.value / base) * 100 });
    }
    serie.push({ id: c.id, lettera: c.lettera, punti });
  }

  return { serie, senzaCurva };
}
